import { ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import clsx from 'clsx';
import { motion, Variants } from 'framer-motion';

import { useWindowSizeStore } from '../../stores/useWindowSizeStore';
import { Button } from '../Button';
import { CaretDownIcon } from '../icons/CaretDownIcon';
import { CartIcon } from '../icons/CartIcon';
import { Select } from '../Select';

type PlanType = 'individual' | 'business';

interface Props {
  plan: {
    type?: string;
    name: string;
    iconSource: string;
    covers: string[];
    coversPlus?: string[];
    category: Category[];
    shortage?: string;
  };
  type: PlanType;
}

interface Category {
  type: string;
  priceNE: string;
  priceAll: string;
  price: string;
  token: string;
}

function getLiVariants(
  isThereShortage: string | undefined,
  width: number,
  type: PlanType,
): Variants {
  if (type === 'individual') {
    return width >= 1548
      ? { open: { height: 790 }, closed: { height: 490 } }
      : {
          open: { height: isThereShortage ? 950 : 995 },
          closed: { height: 490 },
        };
  } else if (type === 'business') {
    return width >= 1548
      ? { open: { height: 880 }, closed: { height: 475 } }
      : {
          open: { height: isThereShortage ? 875 : 995 },
          closed: { height: 385 },
        };
  }
  return width >= 1548
    ? { open: { height: 805 }, closed: { height: 475 } }
    : {
        open: { height: isThereShortage ? 875 : 995 },
        closed: { height: 475 },
      };
}

function figureVariants(type): Variants {
  if (type == 'individual') {
    return {
      open: { height: 420, opacity: 100, marginTop: 24 },
      closed: { height: 0, opacity: 0, marginTop: -8 },
    };
  } else if (type == 'business') {
    return {
      open: { height: 420, opacity: 100, marginTop: 24 },
      closed: { height: 22, opacity: 0 },
    };
  }
  return {
    open: { height: 420, opacity: 100, marginTop: 24 },
    closed: { height: 0, opacity: 0, marginTop: -8 },
  };
}

const buttonVariants: Variants = {
  open: {
    y: '0',
  },
  closed: {
    y: -120,
  },
};

const NORDESSTE_UFS = ['AL', 'PB', 'PE'];

async function fetchPrice(priceUrl: string, tipo: string): Promise<string> {
  try {
    const response = await fetch(priceUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    return data.valor;
  } catch (error) {
    console.error('Não foi possível obter o preço', error);
    return 'Preço não disponível';
  }
}

export function NewPlanCard({ plan, type }: Props): ReactElement {
  const width = useWindowSizeStore(state => state.width);
  const [isOpen, setIsOpen] = useState(false);
  const [categoryPlan, setCategoryPlan] = useState<string>();
  const [NE, setNE] = useState<boolean>(true); // Inicialize como false
  const [price, setPrice] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    async function loadPrice() {
      if (categoryPlan) {
        setLoading(true);
        console.log('plan', plan);
        const value = await getValueByCategory(NE, plan);
        setPrice(value);
        setLoading(false);
      }
    }
    loadPrice();
  }, [categoryPlan, NE]);

  useEffect(() => {
    setCategoryPlan(plan.category[0].type);
  }, [plan.category]);

  const handleCategoryPlanChange = (event: any) => {
    setCategoryPlan(event.target.value);
  };

  function toggleIsOpen() {
    setIsOpen(prevState => !prevState);
  }

  useEffect(() => {
    if (width >= 1300) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [width]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async position => {
          const { latitude, longitude } = position.coords;
          try {
            const apiKey = process.env.REACT_APP_GOOGLE_API_TOKEN;
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`,
            );
            const data = await response.json();
            if (data.results.length > 0) {
              const addressComponents = data.results[0].address_components;
              const ufComponent = addressComponents.find(component =>
                component.types.includes('administrative_area_level_1'),
              );
              const uf = ufComponent ? ufComponent.short_name : null;
              setNE(NORDESSTE_UFS.includes(uf));
            }
          } catch (error) {
            console.error('Failed to fetch UF:', error);
          }
        },
        error => console.error(error.message),
      );
    }
  }, []);

  async function getValueByCategory(NE: boolean, plan: any): Promise<string> {
    const Category = plan.category[0];
    const priceNE = await fetchPrice(Category.priceNE, type);
    const priceAll = await fetchPrice(Category.priceAll, type);
    return NE
      ? priceNE.toString().replace('.', ',')
      : priceAll.toString().replace('.', ',');
  }

  function getCategoryTypes(): string[] {
    return plan.category.map(cat => cat.type);
  }

  function buttomDisable(): boolean {
    return !categoryPlan;
  }

  function linkToGetPlan() {
    const token =
      plan.category.find(cat => cat.type === categoryPlan)?.token || '';
    return type === 'individual'
      ? `/contratar-plano/${token}`
      : `/empresa/contratar-plano/${token}`;
  }

  const handleNavigate = () => {
    setIsNavigating(true);
  };

  return (
    <motion.li
      initial={false}
      animate={isOpen ? 'open' : 'closed'}
      variants={getLiVariants(plan.shortage, width, type)}
      className="overflow-hidden flex flex-col justify-between w-[357px] min-[1548px]:h-[870px] bg-white shadow-card rounded-xl p-5 z-20 max-[1548px]:w-full"
    >
      <div>
        <div className="flex items-center gap-3 h-[89px] bg-[#F7F6FF] px-2 py-14 rounded-xl max-[1548px]:justify-center pb-14">
          <img src={plan.iconSource} alt="Vetor gráfico" />
          <div className="flex flex-col text-primary-main">
            <span className="text-xl">{plan.type ?? 'Plano'}</span>
            <strong className="text-xl font-semibold">{plan.name}</strong>
          </div>
        </div>
        <motion.figure
          initial={false}
          animate={isOpen ? 'open' : 'closed'}
          variants={figureVariants(type)}
          className="flex flex-col gap-2 h-full"
        >
          <figcaption className="font-bold text-primary-main">
            Coberturas
          </figcaption>
          <ul className="flex flex-col text-medium-emphasis text-lg gap-2">
            {plan.coversPlus &&
              plan.coversPlus.map(plus => (
                <li
                  className="text-new-orange font-medium"
                  dangerouslySetInnerHTML={{ __html: plus }}
                  key={plus}
                />
              ))}
            {plan.covers.map(cover => (
              <li key={cover}>{cover}</li>
            ))}
          </ul>
          {/* TODO quais carencias colocar */}
          {plan.shortage && (
            <div className="flex flex-col text-medium-emphasis text-lg mt-4">
              <span className="font-bold text-primary-main">Carência</span>
              <span>{plan.shortage}</span>
            </div>
          )}
        </motion.figure>
      </div>

      {plan.category && plan.category.length > 1 && (
        <div className="py-6">
          <Select
            label="Categoria Plano"
            options={getCategoryTypes()}
            placeholder="Selecione a Categoria"
            value={categoryPlan}
            onChange={handleCategoryPlanChange}
          />
        </div>
      )}

      <div className="flex flex-col items-center gap-2 z-10 bg-white">
        <Link
          to={linkToGetPlan()}
          className="w-full flex"
          style={{ pointerEvents: buttomDisable() ? 'none' : 'auto' }}
        >
          <div className="w-full flex flex-col gap-3 justify-center items-center bg-[#F7F6FF] h-[136px] rounded-xl z-10">
            <span className="text-xl text-primary-main font-semibold">
              Valor mensal
            </span>
            <div className="flex flex-col items-center">
              <span className="text-sm text-medium-emphasis">A partir de</span>
              <span className="text-new-orange">
                <strong className="font-bold">R$</strong>{' '}
                <strong className="text-[2rem]">
                  {loading ? 'Carregando...' : price}
                </strong>
                por mês
              </span>
            </div>
          </div>
        </Link>
        <motion.div
          initial={false}
          animate={isOpen ? 'open' : 'closed'}
          variants={buttonVariants}
          className={clsx(
            !isOpen && 'gap-6',
            'flex flex-col min-[1548px]:w-[222px] max-[1548px]:max-w-[294px]',
          )}
        >
          <Link
            to={linkToGetPlan()}
            className="w-full flex"
            style={{ pointerEvents: isNavigating ? 'none' : 'auto' }}
            onClick={handleNavigate}
          >
            <Button
              text={isNavigating ? 'Carregando...' : 'Contratar plano'}
              Icon={CartIcon}
              className="w-full"
            />
          </Link>
          <Button
            onClick={toggleIsOpen}
            className={clsx(
              'min-[1548px]:hidden ',
              isOpen ? 'mt-4' : 'mt-[53px]',
            )}
            Icon={CaretDownIcon}
            iconClassName={clsx('stroke-primary-main', isOpen && 'rotate-180')}
            intent="tertiary"
            text={isOpen ? 'Ver menos informações' : 'Ver mais informações'}
          />
        </motion.div>
      </div>
    </motion.li>
  );
}
